/**
 * Sales Rep Management — READ-ONLY canonical view.
 *
 * OWNER-TEST-BLOCKER-FIX-PACK (2026-02):
 * Per strict-mode directive, this page is no longer a staff onboarding surface.
 * Adding a sales rep is exclusively done via the canonical Team / Invitations
 * flow in UserManagement.jsx, which writes to `clinic_memberships` and
 * `invitations` via `POST /api/auth/unified/invite`.
 *
 * What was removed (deprecated):
 *  - The "Add Sales Rep" modal with name/email/phone/languages/specializations.
 *  - The legacy `POST /api/sales-reps` call (returns HTTP 410 — Endpoint Deprecated).
 *  - Free-text `languages` / `specializations` inputs (no unstructured data pollution).
 *  - `onSalesRepAdded` / `onAddSalesRep` callbacks.
 *
 * What remains (canonical):
 *  - Read-only roster of provider_sales_rep team members.
 *  - Bulk lead reassignment when a rep leaves (operates on `leads` already in scope).
 *  - "Add Sales Rep" button now routes to `/clinic/admin/users` (UserManagement).
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClinicData, LEAD_STATUSES } from '../../../context/ClinicDataContext';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
  Mail, Star, TrendingUp, Users, CheckCircle, X, Eye,
  UserMinus, ArrowRightLeft, AlertTriangle, UserPlus,
  LayoutGrid, List, ExternalLink,
} from 'lucide-react';
import { toast } from '../../../hooks/use-toast';

const SalesRepManagement = ({ clinicId, salesReps, leads }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { bulkReassignLeads, markSalesRepAsInactive, isSalesRepInactive, getPatientById } = useClinicData();

  const getPatientForLead = (lead) => {
    const patient = getPatientById(lead?.patientId);
    return patient || {
      name: lead?.patient_name || t('provider.team.salesReps.unknownPatient'),
      email: lead?.patient_email || '',
      phone: lead?.patient_phone || '',
      country: lead?.country_of_origin || t('provider.team.salesReps.unknownCountry'),
    };
  };

  const getRepStats = (repId) => {
    const repLeads = (leads || []).filter(l => l.assigned_agent_id === repId);
    const closedWon = repLeads.filter(l =>
      l.status === 'CLOSED_WON' || l.status === 'WON' || l.status === 'COMPLETED'
    ).length;
    const total = repLeads.length;
    return {
      totalLeads: total,
      activeLeads: repLeads.filter(l => !['CLOSED_WON', 'WON', 'COMPLETED', 'LOST'].includes(l.status)).length,
      offersSent: repLeads.filter(l => ['OFFER_SENT', 'QUOTE_SENT', 'NEGOTIATION'].includes(l.status)).length,
      conversionRate: total > 0 ? Math.round((closedWon / total) * 100) : 0,
      byStatus: {
        NEW: repLeads.filter(l => l.status === 'NEW').length,
        ASSIGNED: repLeads.filter(l => l.status === 'ASSIGNED').length,
        CONTACTED: repLeads.filter(l => l.status === 'CONTACTED').length,
        QUALIFIED: repLeads.filter(l => l.status === 'QUALIFIED').length,
        OFFER_SENT: repLeads.filter(l => l.status === 'OFFER_SENT').length,
        QUOTE_SENT: repLeads.filter(l => l.status === 'QUOTE_SENT').length,
        NEGOTIATION: repLeads.filter(l => l.status === 'NEGOTIATION').length,
        WON: repLeads.filter(l => l.status === 'WON').length,
        DEPOSIT_PAID: repLeads.filter(l => l.status === 'DEPOSIT_PAID').length,
        IN_TREATMENT: repLeads.filter(l => l.status === 'IN_TREATMENT').length,
        POST_OP: repLeads.filter(l => l.status === 'POST_OP').length,
        CLOSED_WON: closedWon,
        LOST: repLeads.filter(l => l.status === 'LOST').length,
      },
    };
  };

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [selectedRep, setSelectedRep] = useState(null);
  const [targetRepId, setTargetRepId] = useState('');
  const [reassignLoading, setReassignLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  // OWNER-TEST-BLOCKER-FIX-PACK: route admins to the canonical Team page.
  // No second staff-onboarding surface.
  const goToTeamInvitations = () => {
    navigate('/clinic/admin/users');
  };

  const handleViewRep = (rep) => {
    setSelectedRep(rep);
    setShowDetailModal(true);
  };

  const handleOpenReassign = (rep) => {
    setSelectedRep(rep);
    setTargetRepId('');
    setShowReassignModal(true);
  };

  const getActiveLeadsCount = (repId) =>
    leads.filter(l => l.assigned_agent_id === repId && !['CLOSED_WON', 'LOST'].includes(l.status)).length;

  const handleReassignLeads = async () => {
    if (!targetRepId) {
      toast({ title: t('provider.team.salesReps.selectTargetTitle'), description: t('provider.team.salesReps.selectTargetBody'), variant: 'destructive' });
      return;
    }
    if (targetRepId === selectedRep.id) {
      toast({ title: t('provider.team.salesReps.invalidSelectionTitle'), description: t('provider.team.salesReps.invalidSelectionBody'), variant: 'destructive' });
      return;
    }
    setReassignLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 600));
      const count = bulkReassignLeads(selectedRep.id, targetRepId, clinicId);
      markSalesRepAsInactive(selectedRep.id);
      const targetRep = salesReps.find(r => r.id === targetRepId);
      toast({
        title: t('provider.team.salesReps.reassignedTitle'),
        description: t('provider.team.salesReps.reassignedBody', { count, target: targetRep?.name || t('provider.team.salesReps.selectedRepFallback') }),
      });
      setShowReassignModal(false);
      setSelectedRep(null);
      setTargetRepId('');
    } catch {
      toast({ title: t('provider.team.salesReps.reassignErrorTitle'), description: t('provider.team.salesReps.reassignErrorBody'), variant: 'destructive' });
    } finally {
      setReassignLoading(false);
    }
  };

  const activeReps = salesReps.filter(rep => !isSalesRepInactive(rep.id));
  const inactiveReps = salesReps.filter(rep => isSalesRepInactive(rep.id));

  return (
    <div className="space-y-6" data-testid="sales-rep-management">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('provider.team.salesReps.title')}</h2>
          <p className="text-gray-500">{t('provider.team.salesReps.subtitle')}</p>
        </div>
        <Button data-testid="add-sales-rep-btn" onClick={goToTeamInvitations}>
          <UserPlus className="h-4 w-4 mr-2" />
          {t('provider.team.salesReps.addBtn')}
          <ExternalLink className="h-3 w-3 ml-2 opacity-70" />
        </Button>
      </div>

      <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 flex items-start gap-3" data-testid="canonical-invite-banner">
        <UserPlus className="h-5 w-5 text-teal-600 mt-0.5" />
        <div className="flex-1">
          <p className="font-medium text-teal-900">{t('provider.team.salesReps.canonicalBannerTitle')}</p>
          <p className="text-sm text-teal-700">
            {t('provider.team.salesReps.canonicalBannerBodyPrefix')} <code className="px-1 py-0.5 bg-teal-100 rounded text-xs">provider_sales_rep</code> {t('provider.team.salesReps.canonicalBannerBodySuffix')}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={goToTeamInvitations} data-testid="open-team-page-btn" className="border-teal-300 text-teal-700 hover:bg-teal-100">
          {t('provider.team.salesReps.openTeam')}
        </Button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <ArrowRightLeft className="h-5 w-5 text-blue-600 mt-0.5" />
        <div>
          <p className="font-medium text-blue-900">{t('provider.team.salesReps.reassignBannerTitle')}</p>
          <p className="text-sm text-blue-700">{t('provider.team.salesReps.reassignBannerBody')}</p>
        </div>
      </div>

      {/* Active reps */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">
            {t('provider.team.salesReps.activeHeading', { count: activeReps.length })}
          </h3>
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
            <Button variant={viewMode === 'grid' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('grid')} className={`h-8 px-3 ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`} data-testid="view-grid-btn">
              <LayoutGrid className="h-4 w-4 mr-1" /> {t('provider.team.salesReps.grid')}
            </Button>
            <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('list')} className={`h-8 px-3 ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`} data-testid="view-list-btn">
              <List className="h-4 w-4 mr-1" /> {t('provider.team.salesReps.list')}
            </Button>
          </div>
        </div>

        {viewMode === 'grid' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {activeReps.map(rep => {
              const stats = getRepStats(rep.id);
              const activeLeads = getActiveLeadsCount(rep.id);
              return (
                <Card key={rep.id} className="hover:shadow-md transition-shadow" data-testid={`rep-card-${rep.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={rep.avatar}
                        alt={rep.name}
                        className="w-10 h-10 rounded-full object-cover border-2 border-blue-100"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{rep.name}</p>
                        <p className="text-xs text-gray-500 truncate">{rep.email}</p>
                      </div>
                      <Badge variant="outline" className="bg-green-50 text-green-700 text-xs shrink-0">
                        {t('provider.team.salesReps.activeBadge')}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-4 gap-1 mb-3">
                      <div className="text-center p-1.5 bg-blue-50 rounded"><p className="text-sm font-bold text-blue-600">{stats.totalLeads}</p><p className="text-[10px] text-gray-500">{t('provider.team.salesReps.colTotal')}</p></div>
                      <div className="text-center p-1.5 bg-yellow-50 rounded"><p className="text-sm font-bold text-yellow-600">{stats.activeLeads}</p><p className="text-[10px] text-gray-500">{t('provider.team.salesReps.colActive')}</p></div>
                      <div className="text-center p-1.5 bg-purple-50 rounded"><p className="text-sm font-bold text-purple-600">{stats.offersSent}</p><p className="text-[10px] text-gray-500">{t('provider.team.salesReps.colOffers')}</p></div>
                      <div className="text-center p-1.5 bg-green-50 rounded"><p className="text-sm font-bold text-green-600">{stats.conversionRate}%</p><p className="text-[10px] text-gray-500">{t('provider.team.salesReps.colConv')}</p></div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1 h-8 text-xs" onClick={() => handleViewRep(rep)}>
                        <Eye className="h-3 w-3 mr-1" /> {t('provider.team.salesReps.view')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 h-8 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                        onClick={() => handleOpenReassign(rep)}
                        disabled={activeLeads === 0}
                        data-testid={`reassign-btn-${rep.id}`}
                      >
                        <UserMinus className="h-3 w-3 mr-1" />
                        ({activeLeads})
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {activeReps.length === 0 && (
              <div className="col-span-full text-center py-12 text-gray-500" data-testid="empty-reps-grid">
                <Users className="h-12 w-12 mx-auto opacity-50 mb-3" />
                <p>{t('provider.team.salesReps.emptyGridBody')}</p>
              </div>
            )}
          </div>
        )}

        {viewMode === 'list' && (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="sales-rep-table">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">{t('provider.team.salesReps.colName')}</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">{t('provider.team.salesReps.colEmail')}</th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">{t('provider.team.salesReps.colLeads')}</th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">{t('provider.team.salesReps.colConvRate')}</th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">{t('provider.team.salesReps.colStatus')}</th>
                    <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">{t('provider.team.salesReps.colActions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {activeReps.map(rep => {
                    const stats = getRepStats(rep.id);
                    const activeLeads = getActiveLeadsCount(rep.id);
                    return (
                      <tr key={rep.id} className="hover:bg-gray-50" data-testid={`rep-row-${rep.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <img src={rep.avatar} alt={rep.name} className="w-8 h-8 rounded-full object-cover" />
                            <span className="font-medium text-sm">{rep.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3"><span className="text-sm text-gray-600">{rep.email}</span></td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-sm font-medium">{stats.activeLeads}/{stats.totalLeads}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge
                            variant="outline"
                            className={`text-xs ${stats.conversionRate >= 30 ? 'bg-green-50 text-green-700' : stats.conversionRate >= 15 ? 'bg-yellow-50 text-yellow-700' : 'bg-gray-50 text-gray-600'}`}
                          >
                            {stats.conversionRate}%
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant="outline" className="bg-green-50 text-green-700 text-xs">{t('provider.team.salesReps.activeBadge')}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => handleViewRep(rep)}>
                              <Eye className="h-3 w-3 mr-1" /> {t('provider.team.salesReps.view')}
                            </Button>
                            <Button variant="ghost" size="sm" className="h-8 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50" onClick={() => handleOpenReassign(rep)} disabled={activeLeads === 0}>
                              <UserMinus className="h-3 w-3 mr-1" /> {t('provider.team.salesReps.reassignAction')}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {activeReps.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                        {t('provider.team.salesReps.emptyRowsBody')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Inactive reps */}
      {inactiveReps.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4 text-gray-500">
            {t('provider.team.salesReps.formerHeading', { count: inactiveReps.length })}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {inactiveReps.map(rep => {
              const stats = getRepStats(rep.id);
              return (
                <Card key={rep.id} className="opacity-60">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      <img src={rep.avatar} alt={rep.name} className="w-8 h-8 rounded-full object-cover grayscale" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-600 truncate">{rep.name}</p>
                        <p className="text-xs text-gray-400">{stats.conversionRate}% conv.</p>
                      </div>
                      <Badge variant="outline" className="bg-gray-100 text-gray-500 text-xs">{t('provider.team.salesReps.leftBadge')}</Badge>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Rep Detail Modal */}
      {showDetailModal && selectedRep && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-start gap-4">
              <img
                src={selectedRep.avatar}
                alt=""
                className="w-20 h-20 rounded-full object-cover border-2 border-blue-200"
              />
              <div className="flex-1">
                <CardTitle>{selectedRep.name}</CardTitle>
                <CardDescription>{selectedRep.email}</CardDescription>
                <div className="flex gap-4 mt-2 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <Mail className="h-4 w-4" />
                    {selectedRep.email}
                  </span>
                </div>
              </div>
              <Button variant="ghost" onClick={() => setShowDetailModal(false)}>
                <X className="h-5 w-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold mb-3">{t('provider.team.salesReps.performanceOverview')}</h4>
                <div className="grid grid-cols-4 gap-4 text-center">
                  {(() => {
                    const stats = getRepStats(selectedRep.id);
                    return (
                      <>
                        <div className="bg-blue-50 p-4 rounded-lg"><Users className="h-6 w-6 mx-auto text-blue-500 mb-1" /><p className="text-2xl font-bold text-blue-600">{stats.totalLeads}</p><p className="text-xs text-gray-500">{t('provider.team.salesReps.cardTotalLeads')}</p></div>
                        <div className="bg-yellow-50 p-4 rounded-lg"><TrendingUp className="h-6 w-6 mx-auto text-yellow-500 mb-1" /><p className="text-2xl font-bold text-yellow-600">{stats.activeLeads}</p><p className="text-xs text-gray-500">{t('provider.team.salesReps.cardActive')}</p></div>
                        <div className="bg-purple-50 p-4 rounded-lg"><Star className="h-6 w-6 mx-auto text-purple-500 mb-1" /><p className="text-2xl font-bold text-purple-600">{stats.offersSent}</p><p className="text-xs text-gray-500">{t('provider.team.salesReps.cardOffersSent')}</p></div>
                        <div className="bg-green-50 p-4 rounded-lg"><CheckCircle className="h-6 w-6 mx-auto text-green-500 mb-1" /><p className="text-2xl font-bold text-green-600">{stats.conversionRate}%</p><p className="text-xs text-gray-500">{t('provider.team.salesReps.cardConversion')}</p></div>
                      </>
                    );
                  })()}
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3">{t('provider.team.salesReps.pipelineBreakdown')}</h4>
                <div className="space-y-2">
                  {(() => {
                    const stats = getRepStats(selectedRep.id);
                    const statusOrder = ['NEW', 'ASSIGNED', 'CONTACTED', 'OFFER_SENT', 'DEPOSIT_PAID', 'IN_TREATMENT', 'POST_OP', 'CLOSED_WON', 'LOST'];
                    return statusOrder.map((status) => {
                      const count = stats.byStatus[status] || 0;
                      const statusInfo = LEAD_STATUSES[status];
                      if (!statusInfo) return null;
                      const percentage = stats.totalLeads > 0 ? (count / stats.totalLeads) * 100 : 0;
                      const bgColorClass = statusInfo.color
                        .split(' ')
                        .find(c => c.startsWith('bg-'))
                        ?.replace('-100', '-500') || 'bg-gray-400';
                      return (
                        <div key={status} className="flex items-center gap-3">
                          <div className="w-28 text-sm text-gray-700 font-medium">{statusInfo.label}</div>
                          <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                            <div
                              className={`h-full ${bgColorClass} transition-all duration-300`}
                              style={{ width: `${Math.max(percentage, count > 0 ? 5 : 0)}%` }}
                            />
                          </div>
                          <div className="w-10 text-sm font-semibold text-right">{count}</div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3">{t('provider.team.salesReps.currentAssignedLeads')}</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {leads.filter(l => l.assigned_agent_id === selectedRep.rep_id).slice(0, 10).map(lead => {
                    const statusInfo = LEAD_STATUSES[lead.status];
                    const patient = getPatientForLead(lead);
                    return (
                      <div key={lead.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">{patient.name}</p>
                          <p className="text-sm text-gray-500">{lead.treatment}</p>
                        </div>
                        <Badge className={statusInfo?.color}>{statusInfo?.label}</Badge>
                      </div>
                    );
                  })}
                  {leads.filter(l => l.assigned_agent_id === selectedRep.rep_id).length === 0 && (
                    <p className="text-gray-500 text-center py-4">{t('provider.team.salesReps.noLeadsAssigned')}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reassign Modal */}
      {showReassignModal && selectedRep && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-3 bg-orange-100 rounded-full">
                  <ArrowRightLeft className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <CardTitle>{t('provider.team.salesReps.reassignModalTitle')}</CardTitle>
                  <CardDescription>{t('provider.team.salesReps.reassignModalSubtitle', { name: selectedRep.name })}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-amber-900">{t('provider.team.salesReps.reassignWarnTitle')}</p>
                  <p className="text-sm text-amber-700">{t('provider.team.salesReps.reassignWarnBody', { count: getActiveLeadsCount(selectedRep.id) })}</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-500 mb-2">{t('provider.team.salesReps.leavingRep')}</p>
                <div className="flex items-center gap-3">
                  <img src={selectedRep.avatar} alt="" className="w-12 h-12 rounded-full object-cover" />
                  <div>
                    <p className="font-semibold">{selectedRep.name}</p>
                    <p className="text-sm text-gray-500">{t('provider.team.salesReps.leavingRepLeads', { count: getActiveLeadsCount(selectedRep.id) })}</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('provider.team.salesReps.reassignToLabel')}</label>
                <select value={targetRepId} onChange={(e) => setTargetRepId(e.target.value)} className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-testid="reassign-target-select">
                  <option value="">{t('provider.team.salesReps.reassignSelectPlaceholder')}</option>
                  {activeReps.filter(rep => rep.id !== selectedRep.id).map(rep => (
                    <option key={rep.id} value={rep.id}>
                      {t('provider.team.salesReps.reassignOptionLine', { name: rep.name, count: getActiveLeadsCount(rep.id) })}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">{t('provider.team.salesReps.reassignLeadsListLabel')}</p>
                <div className="max-h-40 overflow-y-auto space-y-2">
                  {leads
                    .filter(l => l.assigned_agent_id === selectedRep.rep_id && !['CLOSED_WON', 'LOST'].includes(l.status))
                    .map(lead => {
                      const statusInfo = LEAD_STATUSES[lead.status];
                      const patient = getPatientForLead(lead);
                      return (
                        <div key={lead.id} className="flex items-center justify-between p-2 bg-white border rounded">
                          <span className="text-sm">{patient.name}</span>
                          <Badge className={`text-xs ${statusInfo?.color}`}>{statusInfo?.label}</Badge>
                        </div>
                      );
                    })
                  }
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowReassignModal(false)} className="flex-1" disabled={reassignLoading}>
                  {t('provider.team.salesReps.cancel')}
                </Button>
                <Button onClick={handleReassignLeads} className="flex-1 bg-orange-600 hover:bg-orange-700" disabled={reassignLoading || !targetRepId} data-testid="reassign-confirm-btn">
                  {reassignLoading ? t('provider.team.salesReps.reassigning') : t('provider.team.salesReps.confirmReassignment')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SalesRepManagement;
